import React, { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  File,
  FileText,
  FileCode,
  Image,
  Film,
  Music,
  FileJson,
  MoreVertical,
  Users,
  Download,
  Trash2,
  Edit2,
  Copy,
  Check,
  Palette,
  Eye,
  X,
  Loader,
  MessageSquare,
  Square,
  CheckSquare,
} from "lucide-react";
import { authFetchJson, apiUrl } from "../../utils/api";
import "./FileItem.css";

// File type icon mapping
const getFileIcon = (name, mimeType) => {
  const ext = name.split(".").pop()?.toLowerCase();

  // By MIME type
  if (mimeType) {
    if (mimeType.startsWith("image/")) return Image;
    if (mimeType.startsWith("video/")) return Film;
    if (mimeType.startsWith("audio/")) return Music;
    if (mimeType.includes("json")) return FileJson;
  }

  // By extension
  const codeExts = ["js", "jsx", "ts", "tsx", "py", "rb", "go", "rs", "java", "c", "cpp", "h", "css", "scss", "html"];
  const textExts = ["txt", "md", "csv", "log"];
  const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "svg", "ico"];
  const videoExts = ["mp4", "webm", "mov", "avi", "mkv"];
  const audioExts = ["mp3", "wav", "m4a", "ogg", "flac", "aac"];

  if (codeExts.includes(ext)) return FileCode;
  if (textExts.includes(ext)) return FileText;
  if (imageExts.includes(ext)) return Image;
  if (videoExts.includes(ext)) return Film;
  if (audioExts.includes(ext)) return Music;

  return File;
};

// Format file size
const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

/**
 * FileItem - Single file or folder row in file tree
 */
const FileItem = ({
  name,
  path,
  type,
  size,
  mimeType,
  lastModified,
  depth = 0,
  isExpanded,
  hasChildren,
  onToggle,
  onRefresh,
  folderColor,
  onColorClick,
  onShareClick,
  isDragOver,
  onUseInChat,
  selectionMode = false,
  isSelected = false,
  onSelect,
}) => {
  const { getToken } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(name);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const isFolder = type === "folder";
  const Icon = isFolder
    ? isExpanded
      ? FolderOpen
      : Folder
    : getFileIcon(name, mimeType);

  // Handle file download
  const handleDownload = async () => {
    try {
      const response = await authFetchJson(
        apiUrl(`/api/c3/files/url/${encodeURIComponent(path)}`)
      );

      if (response.success && response.url) {
        window.open(response.url, "_blank");
      }
    } catch (err) {
      console.error("[FileItem] Download error:", err);
    }
    setShowMenu(false);
  };

  // Check if file is previewable (text, code, images)
  const isPreviewable = () => {
    if (isFolder) return false;
    const ext = name.split(".").pop()?.toLowerCase();
    const textExts = ["txt", "md", "csv", "log", "json", "xml", "yaml", "yml"];
    const codeExts = ["js", "jsx", "ts", "tsx", "py", "rb", "go", "rs", "java", "c", "cpp", "h", "css", "scss", "html", "sql", "sh", "bash"];
    const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
    return textExts.includes(ext) || codeExts.includes(ext) || imageExts.includes(ext);
  };

  // Check if file is an image
  const isImage = () => {
    const ext = name.split(".").pop()?.toLowerCase();
    const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
    return imageExts.includes(ext) || mimeType?.startsWith("image/");
  };

  // Handle file preview
  const handlePreview = async () => {
    setShowMenu(false);
    setShowPreview(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewContent(null);

    try {
      if (isImage()) {
        // For images, get presigned URL
        const response = await authFetchJson(
          apiUrl(`/api/c3/files/url/${encodeURIComponent(path)}`)
        );
        if (response.success && response.url) {
          setPreviewContent({ type: "image", url: response.url });
        } else {
          setPreviewError("Failed to load image");
        }
      } else {
        // For text files, fetch content
        const response = await authFetchJson(
          apiUrl(`/api/c3/files/content/${encodeURIComponent(path)}`)
        );
        if (response.success) {
          setPreviewContent({ type: "text", content: response.content });
        } else {
          setPreviewError(response.error || "Failed to load file");
        }
      }
    } catch (err) {
      console.error("[FileItem] Preview error:", err);
      setPreviewError("Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Copy path to clipboard - includes workspace context for Ollie/Harry
  const handleCopyPath = async () => {
    try {
      // Format path for sharing with AI agents
      const workspacePath = `workspace:/${path}`;
      await navigator.clipboard.writeText(workspacePath);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("[FileItem] Copy error:", err);
    }
    setShowMenu(false);
  };

  // Handle rename
  const handleRename = async () => {
    const trimmedName = newName.trim();
    if (!trimmedName || trimmedName === name) {
      setIsRenaming(false);
      setNewName(name);
      return;
    }

    try {
      const pathParts = path.split("/");
      pathParts[pathParts.length - 1] = trimmedName;
      const newPath = pathParts.join("/");

      const response = await authFetchJson(apiUrl("/api/c3/files/rename"), {
        method: "POST",
        body: JSON.stringify({
          oldPath: path,
          newPath,
          type: isFolder ? "folder" : "file",
        }),
      });

      if (response.success) {
        onRefresh?.();
      }
    } catch (err) {
      console.error("[FileItem] Rename error:", err);
    } finally {
      setIsRenaming(false);
    }
  };

  // Handle file/folder delete
  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    try {
      setIsDeleting(true);
      const deleteUrl = isFolder
        ? apiUrl(`/api/c3/files/${encodeURIComponent(path)}?type=folder`)
        : apiUrl(`/api/c3/files/${encodeURIComponent(path)}`);

      const response = await authFetchJson(deleteUrl, { method: "DELETE" });

      if (response.success) {
        onRefresh?.();
      }
    } catch (err) {
      console.error("[FileItem] Delete error:", err);
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
      setShowMenu(false);
    }
  };

  // Click handler
  const handleClick = (e) => {
    if (selectionMode && !isFolder && onSelect) {
      e.stopPropagation();
      onSelect(e);
    } else if (isFolder && onToggle) {
      onToggle();
    }
  };

  return (
    <div
      className={`file-item ${isFolder ? "folder" : "file"} ${isDragOver ? "drag-over" : ""} ${isSelected ? "selected" : ""}`}
      style={{ paddingLeft: `${depth * 16 + 8}px` }}
    >
      <div className="file-item-content" onClick={handleClick}>
        {/* Selection checkbox (files only, in selection mode) */}
        {selectionMode && !isFolder && (
          <span
            className="file-checkbox"
            onClick={(e) => {
              e.stopPropagation();
              onSelect?.(e);
            }}
          >
            {isSelected ? (
              <CheckSquare size={14} className="checked" />
            ) : (
              <Square size={14} />
            )}
          </span>
        )}

        {/* Expand/collapse chevron for folders */}
        {isFolder ? (
          <span className="file-chevron">
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )
            ) : (
              <span style={{ width: 14 }} />
            )}
          </span>
        ) : !selectionMode ? (
          <span style={{ width: 14 }} />
        ) : null}

        {/* Icon - with folder color */}
        <Icon
          size={16}
          className="file-icon"
          style={isFolder && folderColor ? { color: folderColor } : undefined}
        />

        {/* Name - editable when renaming */}
        {isRenaming ? (
          <input
            type="text"
            className="file-name-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRename();
              if (e.key === "Escape") {
                setIsRenaming(false);
                setNewName(name);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        ) : (
          <span className="file-name" title={`workspace:/${path}`}>
            {name}
          </span>
        )}

        {/* Size (files only) */}
        {!isFolder && size && (
          <span className="file-size">{formatSize(size)}</span>
        )}
      </div>

      {/* Context menu button */}
      <button
        className="file-menu-btn"
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
          setConfirmDelete(false);
        }}
      >
        <MoreVertical size={14} />
      </button>

      {/* Context menu */}
      {showMenu && (
        <>
          <div
            className="file-menu-backdrop"
            onClick={() => {
              setShowMenu(false);
              setConfirmDelete(false);
            }}
          />
          <div className="file-context-menu">
            {/* Use in Chat - insert path into chat input */}
            {onUseInChat && (
              <button onClick={() => {
                setShowMenu(false);
                onUseInChat();
              }}>
                <MessageSquare size={14} />
                <span>Use in Chat</span>
              </button>
            )}

            {/* Copy path */}
            <button onClick={handleCopyPath}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              <span>{copied ? "Copied!" : "Copy Path"}</span>
            </button>

            {/* Preview - previewable files only */}
            {isPreviewable() && (
              <button onClick={handlePreview}>
                <Eye size={14} />
                <span>Preview</span>
              </button>
            )}

            {/* Download - files only */}
            {!isFolder && (
              <button onClick={handleDownload}>
                <Download size={14} />
                <span>Download</span>
              </button>
            )}

            {/* Change color - folders only */}
            {isFolder && onColorClick && (
              <button onClick={() => {
                setShowMenu(false);
                onColorClick();
              }}>
                <Palette size={14} />
                <span>Change Color</span>
              </button>
            )}

            {/* Share with team - folders only */}
            {isFolder && onShareClick && (
              <button onClick={() => {
                setShowMenu(false);
                onShareClick();
              }}>
                <Users size={14} />
                <span>Share with Team…</span>
              </button>
            )}

            {/* Rename */}
            <button onClick={() => {
              setShowMenu(false);
              setIsRenaming(true);
            }}>
              <Edit2 size={14} />
              <span>Rename</span>
            </button>

            {/* Delete */}
            <button
              className={`danger ${confirmDelete ? "confirm" : ""}`}
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <Trash2 size={14} />
              <span>{confirmDelete ? "Confirm delete?" : "Delete"}</span>
            </button>
          </div>
        </>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="file-preview-overlay" onClick={() => setShowPreview(false)}>
          <div className="file-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="file-preview-header">
              <span className="file-preview-title">{name}</span>
              <button className="file-preview-close" onClick={() => setShowPreview(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="file-preview-content">
              {previewLoading && (
                <div className="file-preview-loading">
                  <Loader size={24} className="spinning" />
                  <span>Loading preview...</span>
                </div>
              )}
              {previewError && (
                <div className="file-preview-error">{previewError}</div>
              )}
              {previewContent?.type === "image" && (
                <img
                  src={previewContent.url}
                  alt={name}
                  className="file-preview-image"
                />
              )}
              {previewContent?.type === "text" && (
                <pre className="file-preview-text">{previewContent.content}</pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileItem;
